import { get, patch, post, remove } from './request';
import axiosInstance from './axios-instance';

export enum StandaloneChargeStatusEnum {
  pendingPayment = 'PENDING_PAYMENT',
  paid = 'PAID',
  cancelled = 'CANCELLED',
  expired = 'EXPIRED',
  refunded = 'REFUNDED'
}

export enum StandaloneChargePaymentMethodEnum {
  pix = 'PIX',
  boleto = 'BOLETO'
}

export interface StandaloneChargeCreatedByUserDTO {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
}

export interface StandaloneChargeRefundDTO {
  id: string;
  standaloneChargeId: string;
  createdByUserId: string;
  amount: number;
  description: string;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: StandaloneChargeCreatedByUserDTO;
}

export interface StandaloneChargeCommissionPaymentDTO {
  id?: string;
  collaboratorId?: string;
  commissionPercent: number;
  commissionAmount: number;
  collaboratorName?: string;
  recipientName?: string;
  user?: {
    id?: string;
    name?: string;
  };
  collaborator?: {
    id?: string;
    user?: {
      id?: string;
      name?: string;
    };
  };
}

export interface StandaloneChargeDTO {
  id: string;
  amount: number;
  description: string;
  payerName: string;
  payerType?: 'individual' | 'company' | 'INDIVIDUAL' | 'COMPANY';
  payerCpf: string;
  payerEmail: string;
  payerPhone: string;
  payerAddressStreet?: string;
  payerAddressNumber?: string;
  payerAddressNeighborhood?: string;
  payerAddressComplement?: string;
  payerAddressZipCode?: string;
  payerAddressCity?: string;
  payerAddressState?: string;
  payerAddressCountry?: string;
  status: StandaloneChargeStatusEnum;
  paymentMethod: StandaloneChargePaymentMethodEnum;
  chargeLinkUrl: string;
  createdAt: string;
  expiresAt?: string;
  sentAt?: string;
  cancelledAt?: string;
  refundedAt?: string;
  refundedAmount?: number;
  refundDescription?: string;
  refunds?: StandaloneChargeRefundDTO[];
  estate?: {
    id: string;
    name: string;
  };
  createdByUser?: StandaloneChargeCreatedByUserDTO;
}

/** Resposta de GET /standalone-charges/:id (campos extras do gateway e auditoria). */
export interface StandaloneChargeDetailDTO extends StandaloneChargeDTO {
  chargeLinkToken?: string;
  updatedAt?: string;
  paidAt?: string;
  gateway?: string;
  gatewayChargeId?: string;
  pagarmeOrderId?: string;
  pagarmeChargeId?: string;
  gatewayStatus?: string;
  lastGatewayPayload?: {
    lastEventId?: string;
    lastEventType?: string;
    lastUpdateAt?: string;
    paymentLink?: { id?: string; url?: string; status?: string; expiresAt?: string };
    webhook?: unknown;
  };
  estateSplitPercent?: number;
  platformSplitPercent?: number;
  commissionPayments?: StandaloneChargeCommissionPaymentDTO[];
  pixQrCode?: string;
  pixQrCodeUrl?: string;
  pixCopyPaste?: string;
  pixExpiresAt?: string;
  boletoLine?: string;
  boletoBarcode?: string;
  boletoPdfUrl?: string;
  boletoDueAt?: string;
}

export interface ListStandaloneChargesResponseDTO {
  data: StandaloneChargeDTO[];
  count: number;
  page: number;
}

export interface ListStandaloneChargesFiltersDTO {
  page?: number;
  limit?: number;
  estateId?: string;
  estateUnitId?: string;
  payer?: string;
  document?: string;
  email?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export type StandaloneChargeCommissionCollaboratorStatus =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'AWAITING_BIOMETRY'
  | 'BIOMETRY_REJECTED'
  | 'SUPPORT_REQUIRED';

export type RecipientAttemptOutcome =
  | 'pending'
  | 'active'
  | 'biometry_rejected'
  | 'registration_rejected'
  | 'superseded';

export interface RecipientAttemptSummaryDTO {
  id: string;
  attemptNumber: number;
  pagarmeRecipientId: string;
  outcome: RecipientAttemptOutcome;
  pagarmeStatus?: string;
  kycDetailsStatus?: string;
  kycDetailsStatusReason?: string;
  triggeredBy: 'initial' | 'retry' | 'webhook' | 'sync';
  createdAt?: string;
  closedAt?: string;
}

export interface RecipientBiometryMetaDTO {
  biometryRejected?: boolean;
  supportRequired?: boolean;
  recipientAttemptCount?: number;
  maxRecipientAttempts?: number;
  canRetryBiometry?: boolean;
  recipientAttempts?: RecipientAttemptSummaryDTO[];
}

export interface StandaloneChargeCommissionCollaboratorDTO {
  id: string;
  estateId: string;
  estate?: {
    id?: string;
    name?: string;
  };
  user?: {
    id?: string;
    name?: string;
    email?: string;
    doc?: string;
    cellphone?: string;
  };
  responsable?: {
    id?: string;
    name?: string;
  };
  status: StandaloneChargeCommissionCollaboratorStatus;
  created_at?: string;
  updated_at?: string;
  activation?: {
    recipientType?: ActivationRecipientType;
    registerInformation?: Record<string, unknown>;
    defaultBankAccount?: Record<string, unknown>;
    updatedAt?: string;
    completedAt?: string;
    pagarmeRecipientId?: string;
    lastSyncAt?: string;
    lastSyncStatus?: string;
    lastSyncError?: string;
    payload?: {
      registerInformation?: Record<string, unknown>;
      defaultBankAccount?: Record<string, unknown>;
      register_information?: Record<string, unknown>;
      default_bank_account?: Record<string, unknown>;
    };
  };
}

export interface ListStandaloneChargeCommissionCollaboratorsResponseDTO {
  data: StandaloneChargeCommissionCollaboratorDTO[];
  count: number;
  page: number;
}

export interface ListStandaloneChargeCommissionCollaboratorsFiltersDTO {
  page?: number;
  limit?: number;
  estateId?: string;
  name?: string;
  document?: string;
  userId?: string;
  responsibleId?: string;
  status?: StandaloneChargeCommissionCollaboratorStatus;
}

export interface CreateStandaloneChargeCommissionCollaboratorDTO {
  estateId?: string;
  userId?: string | null;
  activation?: CreateStandaloneChargeActivationDTO;
}

export interface UpdateStandaloneChargeCommissionCollaboratorDTO {
  userId?: string | null;
  status?: StandaloneChargeCommissionCollaboratorStatus;
}

export interface CreateStandaloneChargeDTO {
  estateId?: string;
  commissionCollaboratorIds?: string[];
  amount: number;
  payerName: string;
  payerType?: 'individual' | 'company' | 'INDIVIDUAL' | 'COMPANY';
  payerCpf: string;
  payerEmail: string;
  payerPhone: string;
  payerAddressStreet?: string;
  payerAddressNumber?: string;
  payerAddressNeighborhood?: string;
  payerAddressComplement?: string;
  payerAddressZipCode?: string;
  payerAddressCity?: string;
  payerAddressState?: string;
  payerAddressCountry?: string;
  description: string;
  paymentMethod: StandaloneChargePaymentMethodEnum;
}

export interface CreateStandaloneChargeRefundDTO {
  amount: number;
  description: string;
}

export interface StandaloneChargePublicDTO {
  id: string;
  amount: number;
  description: string;
  payerName: string;
  status: StandaloneChargeStatusEnum;
  paymentMethod: StandaloneChargePaymentMethodEnum;
  expiresAt?: string;
  paidAt?: string;
  pixQrCode?: string;
  pixQrCodeUrl?: string;
  pixCopyPaste?: string;
  pixExpiresAt?: string;
  boletoLine?: string;
  boletoBarcode?: string;
  boletoPdfUrl?: string;
  boletoDueAt?: string;
}

export interface StandaloneChargePublicStatusDTO {
  id: string;
  status: StandaloneChargeStatusEnum;
  paidAt?: string;
  expiresAt?: string;
  paymentMethod: StandaloneChargePaymentMethodEnum;
}

export type ActivationRecipientType = 'individual' | 'corporation';

export enum StandaloneChargeActivationStateEnum {
  notStarted = 'NOT_STARTED',
  active = 'ACTIVE',
  pagarmePending = 'PAGARME_PENDING',
  pagarmeActive = 'PAGARME_ACTIVE',
  pagarmeRejected = 'PAGARME_REJECTED',
  biometryRejected = 'BIOMETRY_REJECTED',
  supportRequired = 'SUPPORT_REQUIRED'
}

export interface StandaloneChargeActivationDTO extends RecipientBiometryMetaDTO {
  id: string;
  status: StandaloneChargeActivationStateEnum;
  recipientType: ActivationRecipientType;
  completedAt?: string;
  updatedAt?: string;
  pagarmeRecipientId?: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  lastSyncError?: string;
  kycRequired?: boolean;
  kycLinkUrl?: string;
  kycQrPayload?: string;
  kycToken?: string;
  registerInformation?: Record<string, unknown>;
  defaultBankAccount?: Record<string, unknown>;
  payload?: {
    registerInformation?: Record<string, unknown>;
    register_information?: Record<string, unknown>;
    defaultBankAccount?: Record<string, unknown>;
    default_bank_account?: Record<string, unknown>;
  };
}

export interface StandaloneChargeActivationStatusDTO {
  estateId: string;
  featureEnabled: boolean;
  standaloneChargesEnabled: boolean;
  activation: StandaloneChargeActivationDTO | null;
}

export interface StandaloneChargeCommissionCollaboratorActivationStatusDTO
  extends RecipientBiometryMetaDTO {
  collaboratorId: string;
  estateId: string;
  status: StandaloneChargeCommissionCollaboratorStatus;
  recipientType?: ActivationRecipientType;
  completedAt?: string;
  updatedAt?: string;
  pagarmeRecipientId?: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  lastSyncError?: string;
  kycRequired: boolean;
  kycLinkUrl?: string;
  kycQrPayload?: string;
  kycToken?: string;
}

export interface CreateStandaloneChargeActivationDTO {
  estateId?: string;
  code?: string;
  recipientType: ActivationRecipientType;
  registerInformation: Record<string, unknown>;
  defaultBankAccount: Record<string, unknown>;
  transferSettings?: Record<string, unknown>;
  automaticAnticipationSettings?: Record<string, unknown>;
  metadata?: unknown;
}

export const listStandaloneCharges = async (
  filters: ListStandaloneChargesFiltersDTO
): Promise<ListStandaloneChargesResponseDTO> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.append(key, String(value));
    }
  });

  return await get(`/standalone-charges?${params.toString()}`);
};

export const exportStandaloneChargesReport = async (
  filters: ListStandaloneChargesFiltersDTO
): Promise<void> => {
  const params = Object.entries(filters).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        acc[key] = String(value);
      }
      return acc;
    },
    {}
  );

  const response = await axiosInstance.get('/standalone-charges/export/xlsx', {
    params,
    responseType: 'blob'
  });

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = 'Relatório de cobranças.xlsx';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const listStandaloneChargeCommissionCollaborators = async (
  filters: ListStandaloneChargeCommissionCollaboratorsFiltersDTO
): Promise<ListStandaloneChargeCommissionCollaboratorsResponseDTO> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.append(key, String(value));
    }
  });

  return await get(`/standalone-charges/commission-collaborators?${params.toString()}`);
};

export type BillingEstateUiStatus =
  | 'ACTIVE'
  | 'BIOMETRY_PENDING'
  | 'BIOMETRY_REJECTED'
  | 'SUPPORT_REQUIRED'
  | 'REGISTRATION_INCOMPLETE'
  | 'REGISTRATION_PENDING';

export type BillingEstateSortColumn =
  | 'activatedAt'
  | 'estateName'
  | 'activeCollaboratorsCount'
  | 'paidChargesCount'
  | 'totalMovedAmount'
  | 'ladoBomRevenueAmount'
  | 'status';

export interface ListStandaloneChargeBillingEstatesFiltersDTO {
  page?: number;
  limit?: number;
  name?: string;
  status?: BillingEstateUiStatus;
  sortBy?: BillingEstateSortColumn;
  sortOrder?: 'ASC' | 'DESC';
}

export interface StandaloneChargeBillingEstateDTO {
  estateId: string;
  estateName: string;
  activatedAt?: string | null;
  activeCollaboratorsCount: number;
  paidChargesCount: number;
  totalMovedAmount: number;
  ladoBomRevenueAmount: number;
  status: BillingEstateUiStatus;
  kycToken?: string;
}

export interface ListStandaloneChargeBillingEstatesResponseDTO {
  data: StandaloneChargeBillingEstateDTO[];
  count: number;
  page: number;
}

export interface StandaloneChargeBillingEstateActivationDetailDTO
  extends RecipientBiometryMetaDTO {
  id: string;
  status: string;
  recipientType: string;
  completedAt?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  pagarmeRecipientId?: string;
  lastSyncAt?: string | null;
  lastSyncStatus?: string;
  lastSyncError?: string;
  kycRequired?: boolean;
  kycToken?: string;
  registerInformation?: Record<string, unknown>;
  defaultBankAccount?: Record<string, unknown>;
}

export interface StandaloneChargeBillingEstateDetailDTO extends StandaloneChargeBillingEstateDTO {
  standaloneChargesEnabled: boolean;
  activation: StandaloneChargeBillingEstateActivationDetailDTO | null;
}

export const listStandaloneChargeBillingEstates = async (
  filters: ListStandaloneChargeBillingEstatesFiltersDTO
): Promise<ListStandaloneChargeBillingEstatesResponseDTO> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.append(key, String(value));
    }
  });

  return await get(`/standalone-charges/billing-estates?${params.toString()}`);
};

export const getStandaloneChargeBillingEstate = async (
  estateId: string
): Promise<StandaloneChargeBillingEstateDetailDTO> => {
  return await get(`/standalone-charges/billing-estates/${estateId}`);
};

export const getStandaloneChargeCommissionCollaborator = async (
  id: string
): Promise<StandaloneChargeCommissionCollaboratorDTO> => {
  return await get(`/standalone-charges/commission-collaborators/${id}`);
};

export const createStandaloneChargeCommissionCollaborator = async (
  payload: CreateStandaloneChargeCommissionCollaboratorDTO
): Promise<StandaloneChargeCommissionCollaboratorDTO> => {
  return await post('/standalone-charges/commission-collaborators', payload);
};

export const updateStandaloneChargeCommissionCollaborator = async (
  id: string,
  payload: UpdateStandaloneChargeCommissionCollaboratorDTO
): Promise<StandaloneChargeCommissionCollaboratorDTO> => {
  return await patch(`/standalone-charges/commission-collaborators/${id}`, payload);
};

export const activateStandaloneChargeCommissionCollaborator = async (
  id: string,
  payload: CreateStandaloneChargeActivationDTO
): Promise<StandaloneChargeActivationDTO> => {
  return await post(
    `/standalone-charges/commission-collaborators/${id}/activation`,
    payload
  );
};

export const getStandaloneChargeCommissionCollaboratorActivationStatus = async (
  id: string
): Promise<StandaloneChargeCommissionCollaboratorActivationStatusDTO> => {
  return await get(
    `/standalone-charges/commission-collaborators/${id}/activation-status`
  );
};

export const deleteStandaloneChargeCommissionCollaborator = async (
  id: string
): Promise<{ success: boolean }> => {
  return await remove(`/standalone-charges/commission-collaborators/${id}`);
};

export const createStandaloneCharge = async (
  payload: CreateStandaloneChargeDTO
): Promise<StandaloneChargeDTO> => {
  return await post('/standalone-charges', payload);
};

export const getStandaloneCharge = async (
  id: string
): Promise<StandaloneChargeDetailDTO> => {
  return await get(`/standalone-charges/${id}`);
};

export const getStandaloneChargeActivationStatus = async (
  estateId?: string
): Promise<StandaloneChargeActivationStatusDTO> => {
  const query = estateId ? `?estateId=${estateId}` : '';
  return await get(`/standalone-charges/activation-status${query}`);
};

export const activateStandaloneCharges = async (
  payload: CreateStandaloneChargeActivationDTO
): Promise<StandaloneChargeActivationStatusDTO> => {
  return await post('/standalone-charges/activation', payload);
};

export const updateStandaloneChargeEstateBankAccount = async (payload: {
  estateId?: string;
  defaultBankAccount: Record<string, unknown>;
}): Promise<StandaloneChargeActivationStatusDTO> => {
  return await patch('/standalone-charges/activation/bank-account', payload);
};

export const updateCommissionCollaboratorBankAccount = async (
  id: string,
  payload: { defaultBankAccount: Record<string, unknown> }
): Promise<StandaloneChargeCommissionCollaboratorActivationStatusDTO> => {
  return await patch(
    `/standalone-charges/commission-collaborators/${id}/bank-account`,
    payload
  );
};

export const retryStandaloneChargeBiometry = async (
  estateId?: string
): Promise<StandaloneChargeActivationStatusDTO> => {
  return await post('/standalone-charges/activation/retry-biometry', {
    ...(estateId ? { estateId } : {})
  });
};

export const retryCommissionCollaboratorBiometry = async (
  collaboratorId: string
): Promise<StandaloneChargeCommissionCollaboratorActivationStatusDTO> => {
  return await post(
    `/standalone-charges/commission-collaborators/${collaboratorId}/activation/retry-biometry`,
    {}
  );
};

export const listEstateRecipientAttempts = async (
  estateId: string
): Promise<RecipientAttemptSummaryDTO[]> => {
  return await get(`/standalone-charges/billing-estates/${estateId}/recipient-attempts`);
};

export const listCommissionCollaboratorRecipientAttempts = async (
  collaboratorId: string
): Promise<RecipientAttemptSummaryDTO[]> => {
  return await get(
    `/standalone-charges/commission-collaborators/${collaboratorId}/recipient-attempts`
  );
};

export const resendStandaloneChargeEmail = async (
  id: string
): Promise<{ success: boolean }> => {
  return await post(`/standalone-charges/${id}/resend-email`, {});
};

export const cancelStandaloneCharge = async (
  id: string
): Promise<StandaloneChargeDTO> => {
  return await patch(`/standalone-charges/${id}/cancel`, {});
};

export const renewStandaloneCharge = async (id: string): Promise<StandaloneChargeDTO> => {
  return await post(`/standalone-charges/${id}/renew`, {});
};

export const createStandaloneChargeRefund = async (
  id: string,
  payload: CreateStandaloneChargeRefundDTO
): Promise<StandaloneChargeRefundDTO> => {
  return await post(`/standalone-charges/${id}/refunds`, payload);
};

export interface StandaloneChargePublicKycDTO {
  status: 'PENDING' | 'COMPLETED' | 'REJECTED';
  kycLinkUrl?: string;
  kycQrPayload?: string;
  recipientName?: string;
}

export const getStandaloneChargePublicKyc = async (
  token: string
): Promise<StandaloneChargePublicKycDTO> => {
  return await get(`/standalone-charges/public/kyc/${token}`);
};

export const getStandaloneChargePublic = async (
  token: string
): Promise<StandaloneChargePublicDTO> => {
  return await get(`/standalone-charges/public/${token}`);
};

export const getStandaloneChargePublicStatus = async (
  token: string
): Promise<StandaloneChargePublicStatusDTO> => {
  return await get(`/standalone-charges/public/${token}/status`);
};
